import {DocumentData} from '@/engine';
import {AdvisorRole} from '@/repositories/beneva-api/endpoints/users';

export interface DocumentStatuses {
  incomplete: Document[];
  waitingSignature: Document[];
  sent: Document[];
}

export interface Document {
  actions: {
    canDelete: boolean;
    canModifySignature: boolean;
    canReinitializeSignature: boolean;
    canRetrySendForSignature: boolean;
    canRetryFinalSubmit: boolean;
  };
  completedAt: string | null;
  createdAt: string;
  forms: DocumentForm[];
  id: string;
  name: string;
  sentForSignatureAt: string | null;
  sentForSignatureFailedAt: string | null;
  signatureStatus: DocumentSignatureStatus | null;
  status: 'incomplete' | 'complete' | 'waitingSignature' | 'waitingSubmit' | 'sent';
  submitFailedAt: string | null;
  submittedAt: string | null;
  type: string;
  updatedAt: string;
  modelVersion: string;
  advisorCode: string | null;
  owner: DocumentContact | null;
  completedBy: DocumentContact | null;
  createdBy: DocumentContact | null;
  sentForSignatureBy: DocumentContact | null;
  updatedBy: DocumentContact | null;
}

export interface DocumentContact {
  firstName: string;
  lastName: string;
  role: AdvisorRole;
}

export enum DocumentSignatureStatus {
  Completed = 'completed',
  Expired = 'expired',
  Declined = 'declined',
  EmailBounced = 'emailBounced',
  SignerLocked = 'signerLocked',
  Activated = 'activated',
  Downloaded = 'downloaded',
  Deleted = 'deleted',
  Trashed = 'trashed',
  Failed = 'failed',
  Sent = 'sent',
  Unknown = 'unknown',
}

export interface DocumentForm {
  id: string;
  type: DocumentFormType;
  filename: string;
  content: string;
  createdAt: string | null;
  updatedAt: string | null;
  signatureFields: string[];
}

export interface FullDocument extends Document {
  sectionUSER: DocumentData['data'];
  sectionCONS: DocumentData['data'];
  sectionINVE: DocumentData['data'];
  sectionCOMP: DocumentData['data'];
  sectionPRV_COMP: DocumentData['data'];
  sectionA: DocumentData['data'];
  sectionB: DocumentData['data'];
  sectionS: DocumentData['data'];
  sectionC: DocumentData['data'];
  sectionD: DocumentData['data'];
  sectionE: DocumentData['data'];
  sectionF: DocumentData['data'];
  sectionH: DocumentData['data'];
  sectionV: DocumentData['data'];
  sectionR: DocumentData['data'];
  sectionI: DocumentData['data'];
  sectionJPeriodic: DocumentData['data'];
  sectionJCash: DocumentData['data'];
  sectionJEFT: DocumentData['data'];
  sectionJTransfer: DocumentData['data'];
  sectionJPlus: DocumentData['data'];
  sectionT: DocumentData['data'];
  sectionX: DocumentData['data'];
  sectionU: DocumentData['data'];
  sectionW: DocumentData['data'];
  sectionM: DocumentData['data'];
  sectionQ: DocumentData['data'];
  sectionL: DocumentData['data'];
  sectionN: DocumentData['data'];
  sectionO: DocumentData['data'];
  sectionY: DocumentData['data'];
  sectionP: DocumentData['data'];
  sectionAA: DocumentData['data'];
  sectionCC: DocumentData['data'];
  sectionDD: DocumentData['data'];
  sectionEE: DocumentData['data'];
}

export type JsonObject = {[Key in string]: JsonValue} & {[Key in string]?: JsonValue | undefined};
export type JsonArray = JsonValue[] | readonly JsonValue[];
export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonArray;

export interface FullDocumentReadyForSignature {
  A: Record<string, JsonValue | undefined>;
  AA: Record<string, JsonValue | undefined>;
  B: Record<string, JsonValue | undefined>;
  C: Record<string, JsonValue | undefined>;
  CC: Record<string, JsonValue | undefined>;
  D: Record<string, JsonValue | undefined>;
  DD: Record<string, JsonValue | undefined>;
  E: Record<string, JsonValue | undefined>;
  EE: Record<string, JsonValue | undefined>;
  F: Record<string, JsonValue | undefined>;
  H: Record<string, JsonValue | undefined>;
  I: Record<string, JsonValue | undefined>;
  J_Cash: Record<string, JsonValue | undefined>;
  J_EFT: Record<string, JsonValue | undefined>;
  J_Periodic: Record<string, JsonValue | undefined>;
  J_Transfer: Record<string, JsonValue | undefined>;
  J_Plus: Record<string, JsonValue | undefined>;
  L: Record<string, JsonValue | undefined>;
  M: Record<string, JsonValue | undefined>;
  N: Record<string, JsonValue | undefined>;
  O: Record<string, JsonValue | undefined>;
  P: Record<string, JsonValue | undefined>;
  Q: Record<string, JsonValue | undefined>;
  R: Record<string, JsonValue | undefined>;
  S: Record<string, JsonValue | undefined>;
  T: Record<string, JsonValue | undefined>;
  U: Record<string, JsonValue | undefined>;
  V: Record<string, JsonValue | undefined>;
  W: Record<string, JsonValue | undefined>;
}

export interface PartialDocumentReadyForResubmittingSignature {
  EE: Record<string, JsonValue | undefined>;
}

export enum DocumentAttachmentId {
  AA2 = 'AA2',
  AA4 = 'AA4',
  AA7 = 'AA7',
  AA10 = 'AA10',
  AA22 = 'AA22',
  AA23 = 'AA23',
  AA24 = 'AA24',
  AA47 = 'AA47',
  AA48 = 'AA48',
  AA28 = 'AA28',
  AA37 = 'AA37',
  AA39 = 'AA39',
  AA41 = 'AA41',
  AA49 = 'AA49',
  AA13 = 'AA13',
  AA14 = 'AA14',
  AA30 = 'AA30',
  AA32 = 'AA32',
  AA44 = 'AA44',
  AA45 = 'AA45',
}

export enum InternalDocumentAttachmentId {
  T_specimen = 'T_specimen',
  N_specimen = 'N_specimen',
  O_specimen = 'O_specimen',
  F_specimen = 'F_specimen',
}

export enum DocumentAttachmentType {
  image = 'image',
  pdf = 'pdf',
  json = 'json',
  unknown = 'unknown',
}

export interface DocumentAttachmentUploadUrl {
  id: string;
  url: string;
  createdAt: string;
  updatedAt: string;
  fieldId: DocumentAttachmentId;
  fileName: string;
  type: DocumentAttachmentType;
  extension: string;
}

export interface DocumentAttachment {
  content: string;
  createdAt: string;
  extension: string;
  fieldId: string;
  fileName: string;
  id: string;
  type: DocumentAttachmentType;
  updatedAt: string;
}

export enum DocumentFormType {
  ind036 = 'ind036',
  ind047 = 'ind047',
  ind136 = 'ind136',
  ind251 = 'ind251',
  k017_k018 = 'k017_k018',
  k043_k044 = 'k043_k044',
  rc725_25 = 'rc725_25',
  t052_t053 = 't052_t053',
  t076_t077 = 't076_t077',
  t086_t087 = 't086_t087',
  t096_t097 = 't096_t097',
  t2033_1 = 't2033_1',
  t2033_2 = 't2033_2',
  t2033_3 = 't2033_3',
  t2033_4 = 't2033_4',
  undefined = 'undefined',
}

export interface DocumentFormPayload {
  fileName: string;
  type: DocumentFormType;
  content: string;
  mappings: Record<string, string | boolean>;
  replacements?: Record<string, string>;
  stamps: Record<string, string>;
  signatures: string[];
}
